import {PerspectiveCamera, Scene, WebGLRenderer, Clock, ColorManagement} from 'three'
import HeroMona from './mona/hero-mona'

export default class Artwork {
  camera = new PerspectiveCamera(20, 1, 0.1, 10)
  renderer: WebGLRenderer
  scene: Scene = new Scene()
  clock: Clock = new Clock()
  pixelRatio: number = 1
  canvas: HTMLCanvasElement
  heroMona: HeroMona
  #width = 0
  #height = 0

  constructor(props: {
    canvas: HTMLCanvasElement
    pixelRatio: number
    reduceMotion: boolean
    onError?: (error: unknown) => void
    onOpeningComplete?: () => void
  }) {
    // Match the modern color handling used by the Three.js r182 source artwork.
    ColorManagement.legacyMode = false

    this.canvas = props.canvas

    const renderer = new WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
    })

    this.renderer = renderer
    renderer.setClearColor(0x000000, 0)
    this.pixelRatio = props.pixelRatio
    renderer.setPixelRatio(this.pixelRatio)

    this.heroMona = new HeroMona(props.reduceMotion, props.onError, props.onOpeningComplete)

    const monaPositionX = this.heroMona.monaGroup.position.x
    this.camera.position.set(monaPositionX, 0, 6)
    this.camera.lookAt(monaPositionX, 0, 0)
    this.camera.updateProjectionMatrix()

    this.scene.add(this.heroMona.group)

    this.clock.start()
  }

  updateRectData() {
    const rect = this.canvas.getBoundingClientRect()

    return rect
  }

  handleMouseMove(e: MouseEvent) {
    const rect = this.updateRectData()
    this.heroMona.handleMouseMove(e, rect, this.camera)
  }

  startOpening() {
    this.heroMona.startOpening()
  }

  resize(width: number, height: number) {
    const nextWidth = Math.round(width)
    const nextHeight = Math.round(height)
    if (nextWidth <= 0 || nextHeight <= 0 || (nextWidth === this.#width && nextHeight === this.#height)) {
      return
    }

    this.#width = nextWidth
    this.#height = nextHeight
    this.renderer.setSize(nextWidth, nextHeight, false)

    const cameraDistance = this.camera.position.z - this.heroMona.monaGroup.position.z
    const verticalFov = (this.camera.fov * Math.PI) / 180
    const viewportHeight = 2 * cameraDistance * Math.tan(verticalFov / 2)
    const compositionOffsetX = (this.heroMona.monaGroup.position.x * nextHeight) / viewportHeight

    this.camera.setViewOffset(nextWidth, nextHeight, -compositionOffsetX, 0, nextWidth, nextHeight)
    this.#render()
  }

  update(isInViewport: boolean, isVisible: boolean) {
    if (!isInViewport || !isVisible) {
      return
    }

    const delta = this.clock.getDelta()

    this.heroMona.update(delta, this.camera)

    this.#render()
  }

  dispose() {
    this.clock.stop()
    this.heroMona.dispose()
    this.scene.clear()
    this.renderer.dispose()
  }

  #render() {
    this.renderer.setRenderTarget(null)
    this.renderer.render(this.scene, this.camera)
  }
}
